import * as React from 'react';
import { FC } from 'react';
import { Checkbox } from '@mui/material';
import { asSnake } from '../../../shared/string';

export interface Option {
    value: string,
    element: React.ReactNode,
}

export interface MultipleCheckboxesProps {
    id?: string,
    values?: string[],
    onChange?: (values: string[]) => void,
    disabled?: boolean,
    highlightChecked?: boolean,
    style?: React.CSSProperties,
    options?: (string|Option)[],
    single?: boolean,
}

const MultipleCheckboxes: FC<MultipleCheckboxesProps> = ({
    id,
    values = [],
    onChange,
    style,
    disabled,
    single,
    options: _options = [],
    highlightChecked,
}) => {

    const options = React.useMemo<Option[]>(
        () => (_options.length && typeof _options[0] !== 'string' ? _options : _options.map(o => ({ value: o, element: o }))) as Option[],
        [_options]);

    const handleChange = React.useCallback((option: string) => () => {
        if (disabled || !onChange) return;
        const newValues = single
            ? (values.includes(option) ? [] : [option])
            : (values.includes(option) ? values.filter(v => v !== option) : values.concat(option));
        onChange(newValues);
    }, [disabled, onChange, values, single]);

    const containerRef = React.useRef<HTMLDivElement>(null);

    React.useLayoutEffect(() => {
        if (!containerRef.current) return;
        const items = Array.from(containerRef.current.children) as HTMLElement[];
        items.forEach(el => el.style.width = '');
        const max = Math.max(...items.map(el => el.offsetWidth));
        if (max > 0) items.forEach(el => el.style.width = `${max}px`);
    }, [options]);

    return (
        <div ref={containerRef} style={{ display: 'flex', flexWrap: 'wrap', columnGap: 'var(--spacer-S)' }}>
            {options.map(option => {
                const checked = values.includes(option.value);
                const inputId = id ? `${id}-${asSnake(option.value)}-checkbox` : undefined;
                return (
                    <label
                        key={option.value}
                        htmlFor={inputId}
                        style={{ display: 'flex', alignItems: 'center', cursor: disabled ? 'default' : 'pointer', margin: 0, ...style }}
                    >
                        <Checkbox
                            id={inputId}
                            disabled={disabled}
                            checked={checked}
                            onChange={handleChange(option.value)}
                            style={{ padding: 'var(--spacer-XXS)' }}
                        />
                        <span style={{ whiteSpace: 'nowrap', backgroundColor: highlightChecked && checked ? '#FFEAC5' : undefined }}>
                            {option.element}
                        </span>
                    </label>
                );
            })}
        </div>
    );
}

export default MultipleCheckboxes;
